import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { PublicFooter, PublicHeader } from '@/components/AppShell'
import { buildMetadataLine } from '@/components/CatalogCard'
import { catalog } from '@/lib/catalog'

/**
 * Render a "dialogue reveals" bullet that may carry a bold lead-in
 * delimited by **…**. Mirrors buildRevealHtml() in
 * scripts/inject-catalog-plugin.ts — keep the two in lockstep.
 */
function renderReveal(text: string): ReactNode {
  const m = text.match(/^\*\*(.+?)\*\*\s*([\s\S]*)$/)
  if (!m) return text
  return (
    <>
      <strong className="text-brand-charcoal">{m[1]}</strong> {m[2]}
    </>
  )
}

/**
 * Per-IND public landing page at /ind/{slug} (A2F-739).
 *
 * SEO surface for a single dossier: metadata + the catalog's
 * description + a signup CTA. The document list itself is auth-gated
 * (portfolios are fetched after login; the public catalog has no
 * portfolio id), so this page links into the signup flow rather than the
 * viewer — same gating model as the landing page.
 *
 * The body markup here is mirrored by `renderIndShell()` in
 * scripts/inject-catalog-plugin.ts so crawlers see the same content in
 * raw HTML. Keep the two in lockstep — any drift produces a hydration
 * flash. Per-page <title>/<meta>/canonical/JSON-LD live in the
 * prerendered head; on client-side nav the title is set via the effect
 * below (commons has no react-helmet).
 */
export function IndPage() {
  const { slug } = useParams<{ slug: string }>()
  const ind = catalog.inds.find((i) => i.slug === slug)

  // On client-side nav (e.g. a "More dossiers" link) only the tab title
  // updates here; canonical/og/Dataset come from the prerendered head and
  // stay describing the first server-served page. That's fine for SEO —
  // crawlers and unfurlers fetch each /ind URL fresh server-side — but it's
  // a known in-app inconsistency (commons has no react-helmet).
  useEffect(() => {
    if (ind) {
      document.title = `${ind.name} — Real IND submission example | CTDCommons`
    }
  }, [ind])

  // Unknown slug. On a *direct* load this branch never runs — vercel.json
  // rewrites /ind/:slug to a file that doesn't exist, so the edge returns a
  // 404 (intended: a clean 404 for a non-existent IND, no soft-redirect).
  // This only fires on client-side nav from a stale <Link>.
  if (!ind) return <Navigate to="/" replace />

  const related = catalog.inds.filter((i) => i.slug !== ind.slug)
  const sponsorLine = ind.sponsor_affiliation
    ? `${ind.sponsor} · ${ind.sponsor_affiliation}`
    : ind.sponsor
  const showCode = !ind.long_name
    .toLowerCase()
    .startsWith(ind.name.toLowerCase())
  const docLine =
    ind.document_count != null
      ? ` — ${ind.document_count.toLocaleString('en-US')} documents`
      : ''

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 py-10 lg:py-14">
          <nav className="text-xs text-slate-500 mb-6">
            <Link to="/" className="hover:text-brand-orange transition-colors">
              CTDCommons
            </Link>
            <span className="mx-1.5 text-slate-300">/</span>
            <span className="text-slate-600">{ind.name}</span>
          </nav>

          <article className="space-y-8">
            <header className="space-y-3">
              {ind.ind_type && (
                <p className="text-[11px] uppercase tracking-wider text-brand-orange font-medium">
                  {ind.ind_type}
                </p>
              )}
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-brand-charcoal leading-tight">
                {showCode ? `${ind.name} — ${ind.long_name}` : ind.long_name}
              </h1>
              <p className="text-sm text-slate-600">
                {sponsorLine} · {ind.indication} · {ind.modality}
              </p>
              <p className="text-xs text-slate-400 uppercase tracking-wider">
                {buildMetadataLine(ind)}
              </p>
            </header>

            <section className="space-y-3">
              <h2 className="text-xl font-semibold text-brand-charcoal">
                About this submission
              </h2>
              <p className="text-sm text-slate-600 leading-relaxed">
                {ind.description_short}
              </p>
              {ind.about_long && (
                <p className="text-sm text-slate-600 leading-relaxed">
                  {ind.about_long}
                </p>
              )}
            </section>

            {ind.fda_quote && (
              <figure className="border-l-2 border-brand-orange/60 pl-5 space-y-2">
                <blockquote className="text-base text-brand-charcoal italic leading-relaxed">
                  “{ind.fda_quote.text}”
                </blockquote>
                <figcaption className="text-xs text-slate-500">
                  — {ind.fda_quote.attribution}
                </figcaption>
              </figure>
            )}

            {ind.dialogue_reveals && ind.dialogue_reveals.length > 0 && (
              <section className="space-y-3">
                <h2 className="text-xl font-semibold text-brand-charcoal">
                  What the FDA dialogue reveals
                </h2>
                <ul className="space-y-3 list-disc pl-5">
                  {ind.dialogue_reveals.map((reveal, i) => (
                    <li
                      key={i}
                      className="text-sm text-slate-600 leading-relaxed"
                    >
                      {renderReveal(reveal)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {ind.best_studied_for && (
              <section className="space-y-2">
                <h2 className="text-xl font-semibold text-brand-charcoal">
                  Best studied for
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {ind.best_studied_for}
                </p>
              </section>
            )}

            <section className="space-y-2">
              <h2 className="text-xl font-semibold text-brand-charcoal">
                Related reading
              </h2>
              <Link
                to="/learn/fda-decoder"
                className="group block rounded-lg border border-slate-200 px-4 py-3 hover:border-brand-orange/60 hover:shadow-sm transition-all"
              >
                <span className="text-sm font-medium text-brand-charcoal group-hover:text-brand-orange transition-colors">
                  Reading Between the Lines: What the FDA Says vs. What It Means
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  A field guide to FDA correspondence, drawn from complete INDs
                  in the archive.
                </span>
              </Link>
            </section>

            <section className="rounded-lg bg-slate-50 px-6 py-6 space-y-3">
              <p className="text-sm text-slate-600 leading-relaxed">
                Browse the complete eCTD package{docLine} across Modules 1–5 —
                cover letter, administrative forms, summaries, CMC, nonclinical,
                and clinical study reports. Free account required.
              </p>
              <Link
                to="/auth/signup"
                className="inline-block px-5 py-2.5 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors"
              >
                Create free account →
              </Link>
            </section>

            {(ind.publication || ind.source_url || ind.license) && (
              <section className="space-y-2 text-sm text-slate-500">
                <p>
                  {ind.source}
                  {ind.source_url && (
                    <>
                      {' · '}
                      <a
                        href={ind.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-orange hover:underline"
                      >
                        source
                      </a>
                    </>
                  )}
                </p>
                {ind.publication && (
                  <p>
                    Companion paper:{' '}
                    <a
                      href={ind.publication.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-orange hover:underline"
                    >
                      {ind.publication.title}
                    </a>{' '}
                    · {ind.publication.venue}
                    {ind.publication.doi ? ` · doi:${ind.publication.doi}` : ''}
                  </p>
                )}
                {ind.license && (
                  <p className="text-xs text-slate-400">
                    Released under {ind.license}
                  </p>
                )}
              </section>
            )}

            {related.length > 0 && (
              <section className="space-y-4 pt-2 border-t border-slate-200">
                <h2 className="text-lg font-semibold text-brand-charcoal pt-6">
                  More dossiers in the archive
                </h2>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link
                        to={`/ind/${r.slug}`}
                        className="group block rounded-lg border border-slate-200 px-4 py-3 hover:border-brand-orange/60 hover:shadow-sm transition-all"
                      >
                        <span className="text-sm font-medium text-brand-charcoal group-hover:text-brand-orange transition-colors">
                          {r.long_name}
                        </span>
                        <span className="block text-xs text-slate-500 mt-0.5">
                          {r.sponsor} · {r.indication}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </article>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}
